import { parseDateKey, type DateKey } from "@/domain/dates";
import type { AnswerValue, Entry, EntryStatus, MedicationIntake, Option, Question, Scenario } from "@/domain/entities";
import { pickText, type Locale } from "@/domain/localized";
import { isEmptyAnswer } from "@/domain/reports/ReportBuilder";
import { isTimePeriod, TIME_PERIOD_OPTIONS } from "@/domain/scenario/timePeriods";
import type { Strings } from "./i18n/strings";

const EMPTY = "—";

function optionText(options: Option[], id: string, locale: Locale): string {
  if (id.startsWith("other:")) return id.slice("other:".length);
  const option = options.find((o) => o.id === id);
  return option ? pickText(option.text, locale) : id;
}

export function formatAnswer(question: Question, value: AnswerValue | undefined, locale: Locale, t: Strings): string {
  if (value === undefined || isEmptyAnswer(question, value)) return EMPTY;
  switch (question.type) {
    case "yesno":
      if (question.labels) return pickText(value === true ? question.labels.yes : question.labels.no, locale);
      return value === true ? t.common.yes : t.common.no;
    case "scale":
    case "number":
      return String(value);
    case "single":
      return optionText(question.options, value as string, locale);
    case "multi":
      return (value as string[]).map((id) => optionText(question.options, id, locale)).join(", ");
    case "time":
      return question.periods && isTimePeriod(value as string) ? optionText(TIME_PERIOD_OPTIONS, value as string, locale) : (value as string);
    case "text":
      return value as string;
    case "medication": {
      const helped = { yes: t.questionnaire.helpedYes, partly: t.questionnaire.helpedPartly, no: t.questionnaire.helpedNo };
      return (value as MedicationIntake[])
        .map((intake) => {
          const dose = intake.dose ? ` ${intake.dose}` : "";
          const outcome = intake.helped ? `, ${t.questionnaire.helpedShort}: ${helped[intake.helped]}` : "";
          return `${intake.name}${dose} ×${intake.count}${outcome}`;
        })
        .join("; ");
    }
  }
}

/** Short line for one attack: its start time and intensity, when answered. */
export function formatEpisode(entry: Entry, scenario: Scenario, locale: Locale): string {
  const parts: string[] = [];
  // Whatever the scenario calls its first time question (startTime, bedTime, ...).
  const timeQuestion = scenario.questions.find((q) => q.type === "time");
  const start = timeQuestion ? entry.answers[timeQuestion.id] : undefined;
  if (typeof start === "string" && start !== "") parts.push(isTimePeriod(start) ? optionText(TIME_PERIOD_OPTIONS, start, locale) : start);
  const intensityId = scenario.calendarMarker?.intensityQuestionId;
  const intensityQuestion = intensityId ? scenario.questions.find((q) => q.id === intensityId) : undefined;
  const intensity = intensityId ? entry.answers[intensityId] : undefined;
  if (intensityQuestion?.type === "scale" && typeof intensity === "number" && !Number.isNaN(intensity)) {
    parts.push(`${intensity}/${intensityQuestion.max}`);
  }
  return parts.join(" · ");
}

function utcDate(key: DateKey): Date {
  const { year, month, day } = parseDateKey(key);
  return new Date(Date.UTC(year, month - 1, day));
}

export function formatDate(key: DateKey, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }).format(utcDate(key));
}

/** "9 September, 14:02" in the user's language, local time. */
export function formatDateTime(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

export function monthTitle(year: number, month: number, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(year, month - 1, 1)));
}

/** Monday first. */
export function weekdayNames(locale: Locale): string[] {
  const monday = Date.UTC(2024, 0, 1);
  const formatter = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
  return Array.from({ length: 7 }, (_, i) => formatter.format(new Date(monday + i * 86_400_000)));
}

/** Only finished and skipped entries have a name; a draft reads as no entry yet. */
export function statusLabel(status: EntryStatus | undefined, t: Strings): string {
  return status === "complete" ? t.today.complete : status === "skipped" ? t.today.skipped : t.today.pending;
}
